import React, { useState, useRef } from 'react';
import { ChevronDown, ChevronRight, Edit3, Save, X, Calculator, AlertCircle } from 'lucide-react';
import { getDatabase, ref, set } from 'firebase/database';
import { getAuth } from 'firebase/auth';
import { Building as BuildingType } from '../../types/protocols';
import { formatDateForStorage } from '../../Helpers/DateUtils';
import { BaseType } from '../../types/Enums';
import { translate } from '../../types/EnumTranslations';
import { ComponentLifetime } from '../Förvaltning/TemplatesEditor/types';
import { BaseComponentImpl } from '../../types/implementations';
import { recordActivity } from '../../api/activityApi';

interface DepreciationTabProps {
    buildings: Record<string, BuildingType>;
    componentLifetimes: Record<string, ComponentLifetime>;
    activePlans: Record<string, any>;
    onDataRefresh?: () => void;
    expandedBuildings: Set<string>;
    setExpandedBuildings: React.Dispatch<React.SetStateAction<Set<string>>>;
    expandedBaseTypes: Set<string>;
    setExpandedBaseTypes: React.Dispatch<React.SetStateAction<Set<string>>>;
}

interface ComponentDepreciation {
    component: any;
    buildingKey: string;
    buildingName: string;
    warrantyStartYear?: number;  // Derived from warrantyStartDate (YYYY-MM-DD)
    expectedLifetime?: number;
    replacementCost?: number;
    depreciationCost?: number;
    missingData: string[];
}

const DepreciationTab: React.FC<DepreciationTabProps> = ({
    buildings,
    componentLifetimes,
    activePlans,
    onDataRefresh,
    expandedBuildings,
    setExpandedBuildings,
    expandedBaseTypes,
    setExpandedBaseTypes
}) => {
    const [editingWarrantyStart, setEditingWarrantyStart] = useState<{ buildingKey: string; componentKey: string } | null>(null);
    const [editValue, setEditValue] = useState<string>('');
    const [saving, setSaving] = useState<boolean>(false);
    const scrollPositionRef = useRef<number>(0);

    const db = getDatabase();

    // Get buildings with active maintenance plans
    const buildingsWithActivePlans = Object.keys(activePlans).map(buildingKey => {
        const building = buildings[buildingKey];
        return building ? { key: buildingKey, building } : null;
    }).filter(Boolean) as { key: string; building: BuildingType }[];

    const toggleBuilding = (buildingKey: string) => {
        const newExpanded = new Set(expandedBuildings);
        if (newExpanded.has(buildingKey)) {
            newExpanded.delete(buildingKey);
        } else {
            newExpanded.add(buildingKey);
        }
        setExpandedBuildings(newExpanded);
    };

    const toggleBaseType = (buildingKey: string, baseType: string) => {
        const baseTypeKey = `${buildingKey}-${baseType}`;
        const newExpanded = new Set(expandedBaseTypes);
        if (newExpanded.has(baseTypeKey)) {
            newExpanded.delete(baseTypeKey);
        } else {
            newExpanded.add(baseTypeKey);
        }
        setExpandedBaseTypes(newExpanded);
    };

    const calculateDepreciation = (component: any, buildingKey: string): ComponentDepreciation => {
        const missingData: string[] = [];
        let depreciationCost: number | undefined;

        // Get component lifetime data
        const componentType = component.specificType;
        const lifetime = componentType ? componentLifetimes[componentType] : null;

        // Check for warranty start date (use year from YYYY-MM-DD format)
        const warrantyStartDate = component.warrantyStartDate?.value;
        const warrantyStartYear = warrantyStartDate ? parseInt(warrantyStartDate.substring(0, 4), 10) : undefined;
        if (!warrantyStartYear) {
            missingData.push('Garantistart');
        }

        // Check for expected lifetime
        const expectedLifetime = lifetime?.expectedLifetimeYears;
        if (!expectedLifetime) {
            missingData.push('Förväntad livslängd');
        }

        // Check for replacement cost
        const replacementCost = lifetime?.replacementCost;
        if (!replacementCost) {
            missingData.push('Ersättningskostnad');
        }

        // Calculate depreciation if we have all required data
        if (warrantyStartYear && expectedLifetime && replacementCost) {
            const currentYear = new Date().getFullYear();
            const yearsSinceInstall = currentYear - warrantyStartYear;
            const yearsRemaining = Math.max(0, expectedLifetime - yearsSinceInstall);


            if (yearsRemaining > 0) {
                depreciationCost = replacementCost / yearsRemaining;
            } else {
                depreciationCost = 0; // Component has reached end of life
            }
        }

        return {
            component,
            buildingKey,
            buildingName: buildings[buildingKey]?.summary?.name || 'Okänd byggnad',
            warrantyStartYear,
            expectedLifetime,
            replacementCost,
            depreciationCost,
            missingData
        };
    };

    const groupComponentsByBaseType = (components: any[]) => {
        const grouped: Record<string, any[]> = {};
        
        components.forEach(component => {
            const baseType = component.type;
            if (!grouped[baseType]) {
                grouped[baseType] = [];
            }
            grouped[baseType].push(component);
        });

        return grouped;
    };

    const startEditingWarrantyStart = (buildingKey: string, componentKey: string, currentValue?: string) => {
        setEditingWarrantyStart({ buildingKey, componentKey });
        setEditValue(currentValue || '');
    };

    const saveWarrantyStart = async () => {
        if (!editingWarrantyStart) return;

        const user = getAuth().currentUser;
        if (!user) {
            alert('Du måste vara inloggad för att redigera');
            return;
        }

        setSaving(true);
        try {
            const warrantyStartRef = ref(db, `buildings/${editingWarrantyStart.buildingKey}/structure/components/${editingWarrantyStart.componentKey}/warrantyStartDate`);

            // Validate date format YYYY-MM-DD
            const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
            if (!dateRegex.test(editValue)) {
                alert('Ange datum i format ÅÅÅÅ-MM-DD');
                return;
            }

            const year = parseInt(editValue.substring(0, 4), 10);
            if (year < 1900 || year > new Date().getFullYear() + 10) {
                alert('Ange ett giltigt år mellan 1900 och ' + (new Date().getFullYear() + 10));
                return;
            }

            await set(warrantyStartRef, {
                value: editValue,
                status: 'set',
                updatedBy: user.uid,
                updatedWhen: formatDateForStorage(new Date())
            });
            recordActivity('component.updated', { buildingKey: editingWarrantyStart.buildingKey, targetKey: editingWarrantyStart.componentKey });

            setEditingWarrantyStart(null);
            setEditValue('');

            // Save current scroll position
            scrollPositionRef.current = window.scrollY;

            // Refresh the data to show the updated warranty start
            if (onDataRefresh) {
                onDataRefresh();

                // Restore scroll position after a short delay to allow for re-render
                setTimeout(() => {
                    window.scrollTo(0, scrollPositionRef.current);
                }, 100);
            }
        } catch (error) {
            console.error('Error saving warranty start date:', error);
            alert('Ett fel uppstod vid sparning av garantistart');
        } finally {
            setSaving(false);
        }
    };

    const cancelEditing = () => {
        setEditingWarrantyStart(null);
        setEditValue('');
    };

    if (buildingsWithActivePlans.length === 0) {
        return (
            <div className="no-plans-section">
                <div className="alert alert-info">
                    <h4 className="alert-heading">Inga aktiva underhållsplaner</h4>
                    <p className="mb-0">
                        Det finns inga byggnader med aktiva underhållsplaner att visa avskrivningar för.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="depreciation-tab ekonomi-depr-tab">
            <div className="depreciation-header mb-4">
                <h3>Avskrivningar per byggnad</h3>
                <p className="text-muted">
                    Avskrivningskostnad = Ersättningskostnad ÷ Återstående livslängd
                </p>
            </div>

            <div className="buildings-list">
                {buildingsWithActivePlans.map(({ key: buildingKey, building }) => {
                    const isExpanded = expandedBuildings.has(buildingKey);
                    const components = building.model?.structure?.components || {};
                    const componentList = Object.entries(components).map(([componentKey, component]) => ({
                        ...component,
                        key: componentKey
                    }));

                    const componentDepreciations = componentList.map(component => 
                        calculateDepreciation(component, buildingKey)
                    );

                    const totalDepreciation = componentDepreciations.reduce((sum, dep) => 
                        sum + (dep.depreciationCost || 0), 0
                    );

                    return (
                        <div key={buildingKey} className="building-section std-box dropshadow mb-3">
                            {/* Accordion header: a button inside the heading. Own ekonomi-depr-*
                                classes, since .building-header is also styled by ArchiveDashboard.css.
                                Name and tags share a row on desktop; on phones the tags move below
                                the name (see EkonomiPage.css). */}
                            <h4 className="ekonomi-depr-heading">
                                <button
                                    type="button"
                                    className="ekonomi-depr-building-header"
                                    onClick={() => toggleBuilding(buildingKey)}
                                    aria-expanded={isExpanded}
                                >
                                    <span className="ekonomi-depr-building-title">
                                        {isExpanded
                                            ? <ChevronDown size={20} aria-hidden="true" />
                                            : <ChevronRight size={20} aria-hidden="true" />}
                                        <span className="ekonomi-depr-building-name">{building.summary?.name || 'Okänd byggnad'}</span>
                                    </span>
                                    <span className="ekonomi-depr-building-tags">
                                        <span className="badge bg-primary">
                                            {componentList.length} komponenter
                                        </span>
                                        <span className="badge bg-success">
                                            <Calculator size={16} className="me-1" aria-hidden="true" />
                                            {Math.round(totalDepreciation).toLocaleString('sv-SE')} kr/år
                                        </span>
                                    </span>
                                </button>
                            </h4>

                            {isExpanded && (
                                <div className="building-content p-3 border rounded">
                                    {Object.entries(groupComponentsByBaseType(componentList)).map(([baseType, components]) => {
                                        const baseTypeKey = `${buildingKey}-${baseType}`;
                                        const isBaseTypeExpanded = expandedBaseTypes.has(baseTypeKey);
                                        
                                        return (
                                            <div key={baseType} className="base-type-section mb-4">
                                                <div 
                                                    className="base-type-header d-flex align-items-center justify-content-between p-2 bg-light rounded cursor-pointer"
                                                    onClick={() => toggleBaseType(buildingKey, baseType)}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    <div className="d-flex align-items-center">
                                                        {isBaseTypeExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                                                        <h5 className="mb-0 ms-2">
                                                            {translate(baseType as BaseType)}
                                                        </h5>
                                                    </div>
                                                    <span className="badge bg-secondary">
                                                        {components.length} komponenter
                                                    </span>
                                                </div>
                                                
                                                {isBaseTypeExpanded && (
                                                    <div className="components-list mt-3">
                                                {components.map(component => {
                                                    const depreciation = componentDepreciations.find(dep => dep.component.key === component.key);
                                                    if (!depreciation) return null;
                                                    
                                                    const componentImpl = new BaseComponentImpl(component);

                                                    return (
                                                        <div key={component.key} className="component-item std-box dropshadow p-3 mb-2">
                                                            <div className="d-flex justify-content-between align-items-start">
                                                                <div className="component-info">
                                                                    <h6 className="mb-1">
                                                                        {componentImpl.displayName(building)}
                                                                    </h6>
                                                                    {component.specificType && (
                                                                        <small className="text-muted">
                                                                            {translate(component.specificType)}
                                                                        </small>
                                                                    )}
                                                                </div>
                                                                
                                                                <div className="component-depreciation text-end">
                                                                    {depreciation.depreciationCost !== undefined ? (
                                                                        <div>
                                                                            <div className="depreciation-cost h5 text-success mb-1">
                                                                                {Math.round(depreciation.depreciationCost).toLocaleString('sv-SE')} kr/år
                                                                            </div>
                                                                            <small className="text-muted">
                                                                                Garantistart: {depreciation.warrantyStartYear} |
                                                                                Livslängd: {depreciation.expectedLifetime} år |
                                                                                Ersättning: {Math.round(depreciation.replacementCost || 0).toLocaleString('sv-SE')} kr
                                                                            </small>
                                                                        </div>
                                                                    ) : (
                                                                        <div className="missing-data">
                                                                            <div className="d-flex align-items-center text-warning mb-1">
                                                                                <AlertCircle size={16} className="me-1" />
                                                                                <span>Kan inte beräkna avskrivning</span>
                                                                            </div>
                                                                            <small className="text-muted">
                                                                                Saknar: {depreciation.missingData.join(', ')}
                                                                            </small>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            {/* Warranty Start Date Edit Section */}
                                                            <div className="warranty-start-section mt-2 pt-2 border-top">
                                                                <div className="d-flex align-items-center justify-content-between">
                                                                    <div>
                                                                        <small className="text-muted">Garantistart:</small>
                                                                        {editingWarrantyStart?.buildingKey === buildingKey && editingWarrantyStart?.componentKey === component.key ? (
                                                                            <div className="d-flex align-items-center mt-1">
                                                                                <input
                                                                                    type="date"
                                                                                    className="form-control form-control-sm me-2"
                                                                                    style={{ width: '150px' }}
                                                                                    value={editValue}
                                                                                    onChange={(e) => setEditValue(e.target.value)}
                                                                                />
                                                                                <button
                                                                                    className="btn btn-success btn-sm me-1"
                                                                                    onClick={saveWarrantyStart}
                                                                                    disabled={saving}
                                                                                >
                                                                                    <Save size={14} />
                                                                                </button>
                                                                                <button
                                                                                    className="btn btn-secondary btn-sm"
                                                                                    onClick={cancelEditing}
                                                                                    disabled={saving}
                                                                                >
                                                                                    <X size={14} />
                                                                                </button>
                                                                            </div>
                                                                        ) : (
                                                                            <div className="d-flex align-items-center mt-1">
                                                                                <span className="me-2">
                                                                                    {component.warrantyStartDate?.value || 'Ej angivet'}
                                                                                </span>
                                                                                <button
                                                                                    className="btn btn-outline-primary btn-sm"
                                                                                    onClick={() => startEditingWarrantyStart(buildingKey, component.key, component.warrantyStartDate?.value)}
                                                                                >
                                                                                    <Edit3 size={14} />
                                                                                </button>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default DepreciationTab;
